/**
 * The route line styling in an embed. An embed has no signed-in viewer, so it
 * can't read the owner's "customise route style" settings the way the map does;
 * the Embed Map builder starts from those settings and writes any route type
 * that differs from the defaults into the URL — `route_main=9E00FF-2-80` is
 * colour, thickness multiplier and opacity percent, the same three controls as
 * the map's Customise route style dialog — and the embed paints them.
 */
import {
  DEFAULT_ROUTE_STYLE_SETTINGS,
  getRouteStyleSettings,
  type RouteStyleSettings,
  type RouteTypePrefs,
} from '../routeStyleSettings';

export type EmbedRouteType = 'main' | 'alt' | 'spur' | 'recording';
export type EmbedRouteStyles = Record<EmbedRouteType, RouteTypePrefs>;
export type EmbedRouteStyleOverrides = Partial<Record<EmbedRouteType, Partial<RouteTypePrefs>>>;

export const EMBED_ROUTE_TYPES: EmbedRouteType[] = ['main', 'alt', 'spur', 'recording'];

/** The thickness multipliers the Customise route style dialog offers. */
export const THICKNESS_OPTIONS = [0.5, 1, 2, 3];
export const MIN_OPACITY = 10;
export const MAX_OPACITY = 100;

const HEX_COLOR = /^#?([0-9a-f]{6})$/i;

/** `#RRGGBB` (upper case) for a six-digit hex colour with or without the `#`; null for anything else. */
export function normalizeRouteColor(value: string | null | undefined): string | null {
  const match = HEX_COLOR.exec((value || '').trim());
  return match ? `#${match[1].toUpperCase()}` : null;
}

/** The URL param name for a route type. */
export function routeStyleParamName(type: EmbedRouteType): string {
  return `route_${type}`;
}

function cloneStyles(source: Pick<RouteStyleSettings, EmbedRouteType>): EmbedRouteStyles {
  return {
    main: { ...source.main },
    alt: { ...source.alt },
    spur: { ...source.spur },
    recording: { ...source.recording },
  };
}

/**
 * What an embed draws when the URL carries no route style. A recording's track has always been the
 * main route's red here (the map's own Recording style is purple), and embeds already pasted into
 * other sites must not change colour, so that stays the default. The builder writes the owner's
 * Recording style into any new embed, so a new one still matches their map.
 */
export function defaultRouteStyles(): EmbedRouteStyles {
  return { ...cloneStyles(DEFAULT_ROUTE_STYLE_SETTINGS), recording: { ...DEFAULT_ROUTE_STYLE_SETTINGS.main } };
}

/** The styles the owner uses on their own map, which is where the builder starts. */
export function ownerRouteStyles(settings: RouteStyleSettings = getRouteStyleSettings()): EmbedRouteStyles {
  const owner = cloneStyles(settings);
  EMBED_ROUTE_TYPES.forEach((type) => {
    owner[type] = sanitizePrefs(owner[type], DEFAULT_ROUTE_STYLE_SETTINGS[type]);
  });
  return owner;
}

function sanitizePrefs(prefs: Partial<RouteTypePrefs> | null | undefined, fallback: RouteTypePrefs): RouteTypePrefs {
  const color = normalizeRouteColor(prefs?.color) ?? fallback.color;
  const thickness = THICKNESS_OPTIONS.includes(Number(prefs?.thickness)) ? Number(prefs?.thickness) : fallback.thickness;
  const opacity = Number.isFinite(Number(prefs?.opacity))
    ? Math.max(MIN_OPACITY, Math.min(MAX_OPACITY, Math.round(Number(prefs?.opacity))))
    : fallback.opacity;
  return { color, thickness, opacity };
}

function samePrefs(a: RouteTypePrefs, b: RouteTypePrefs): boolean {
  return normalizeRouteColor(a.color) === normalizeRouteColor(b.color) && a.thickness === b.thickness && a.opacity === b.opacity;
}

/** `9E00FF-2-80`: colour (no #), thickness multiplier, opacity percent. */
export function formatRouteStyleParam(prefs: RouteTypePrefs): string {
  const clean = sanitizePrefs(prefs, DEFAULT_ROUTE_STYLE_SETTINGS.main);
  return `${clean.color.slice(1)}-${clean.thickness}-${clean.opacity}`;
}

/** The prefs a `route_*` param value holds, or null if it is not exactly that shape. */
export function parseRouteStyleParam(value: string | null | undefined): RouteTypePrefs | null {
  const match = /^([0-9a-f]{6})-(\d+(?:\.\d+)?)-(\d{1,3})$/i.exec((value || '').trim());
  if (!match) return null;
  const thickness = Number(match[2]);
  const opacity = Number(match[3]);
  if (!THICKNESS_OPTIONS.includes(thickness) || opacity < MIN_OPACITY || opacity > MAX_OPACITY) return null;
  return { color: `#${match[1].toUpperCase()}`, thickness, opacity };
}

/**
 * The `route_*` params for the route types whose style differs from the embed's
 * default, so an unstyled embed stays a bare URL. Types a mode doesn't draw
 * (a recording has no alt or spur routes) are left out.
 */
export function routeStyleParams(
  styles: EmbedRouteStyles | null | undefined,
  types: EmbedRouteType[] = EMBED_ROUTE_TYPES,
): Array<[string, string]> {
  if (!styles) return [];
  const defaults = defaultRouteStyles();
  return types
    .filter((type) => styles[type] && !samePrefs(sanitizePrefs(styles[type], defaults[type]), defaults[type]))
    .map((type) => [routeStyleParamName(type), formatRouteStyleParam(styles[type])]);
}

/** The settings an embed paints with: the defaults, with whatever the URL overrode on top. */
export function embedRouteStyleSettings(overrides: EmbedRouteStyleOverrides | null | undefined): RouteStyleSettings {
  const base = defaultRouteStyles();
  EMBED_ROUTE_TYPES.forEach((type) => {
    base[type] = sanitizePrefs({ ...base[type], ...(overrides?.[type] || {}) }, base[type]);
  });
  return { ...base, hideMileMarkers: DEFAULT_ROUTE_STYLE_SETTINGS.hideMileMarkers };
}
